import * as React from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Upload, AlertCircle } from "lucide-react";
import { authService } from "@/services/authService";
import type { User } from "@/services/authService";
import { toast } from "sonner";
import { PhotoCropDialog } from "@/components/common/PhotoCropDialog";

interface PhotosStepProps {
  userData: User | null;
  isFotoRejected: boolean;
  onSuccess: () => void;
  onError: (msg: string) => void;
  onConsentNeeded: (fn: () => Promise<void>, label: string) => void;
  onSkip: () => void;
}

export function PhotosStep({ userData, isFotoRejected, onSuccess, onError, onConsentNeeded, onSkip }: PhotosStepProps) {
  const [fotoFile, setFotoFile] = React.useState<File | null>(null);
  const [fotoPreview, setFotoPreview] = React.useState<string | null>(null);
  const [showCropDialog, setShowCropDialog] = React.useState(false);
  const [cropImageData, setCropImageData] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  const handleFileChange = (file: File | null) => {
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("File harus berupa gambar");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const imageData = reader.result as string;
      setCropImageData(imageData);
      setShowCropDialog(true);
    };
    reader.readAsDataURL(file);
  };

  const handlePhotoCropComplete = (croppedImage: string) => {
    setFotoPreview(croppedImage);
    fetch(croppedImage)
      .then((res) => res.blob())
      .then((blob) => {
        const file = new File([blob], "foto.jpg", { type: "image/jpeg" });
        setFotoFile(file);
      });
  };

  const runSubmit = async () => {
    if (!fotoFile) return;

    setIsLoading(true);
    try {
      await authService.uploadPhotos(fotoFile, undefined);
      onSuccess();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Gagal mengunggah foto";
      setError(msg);
      onError(msg);
      toast.error("Gagal mengunggah foto", { description: msg });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");

    if (!fotoFile) {
      setError("Silakan upload foto profil");
      return;
    }

    onConsentNeeded(runSubmit, "Unggah foto profil");
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {isFotoRejected && userData?.fotoRejectionReason && (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3">
          <AlertCircle className="h-4 w-4 mt-0.5 text-destructive flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-destructive">Foto profil Anda tidak memenuhi kriteria</p>
            <p className="text-sm text-destructive/80">{userData.fotoRejectionReason}</p>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <Upload className="h-4 w-4" />
          Foto Profil
        </Label>
        <div className="relative">
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
            disabled={isLoading}
            className="hidden"
            id="foto-input"
          />
          <label
            htmlFor="foto-input"
            className="flex h-32 w-full items-center justify-center rounded-lg border-2 border-dashed border-muted-foreground bg-muted/50 p-4 cursor-pointer hover:bg-muted transition-colors"
          >
            {fotoPreview ? (
              <img src={fotoPreview} alt="Preview" className="h-full object-cover rounded" />
            ) : (
              <div className="text-center">
                <Upload className="h-6 w-6 mx-auto text-muted-foreground mb-1" />
                <p className="text-xs text-muted-foreground">Klik untuk upload foto profil</p>
              </div>
            )}
          </label>
        </div>
        {fotoFile && <p className="text-xs text-green-600">✓ {fotoFile.name}</p>}
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 p-3 rounded-md">
          <AlertCircle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex gap-2">
        <Button type="submit" className="flex-1" disabled={isLoading || !fotoFile}>
          {isLoading ? "Mengunggah..." : "Selesai"}
        </Button>
      </div>

      <PhotoCropDialog
        open={showCropDialog}
        onOpenChange={setShowCropDialog}
        imageData={cropImageData || ""}
        onCropComplete={handlePhotoCropComplete}
        aspectRatio={1}
        title="Crop Foto Profil"
      />
    </form>
  );
}
